// T-407: helper condivisi per il flusso "foto etichetta → lotto/scadenza".
// Usati sia dal carico singolo sia dalla CODA in background (useLabelLoadQueue).
import { getSupabase, SUPABASE_URL } from '../supabase.js';

export interface LotExpiry {
  batch_lot: string | null;
  expiration_date: string | null;
  confidence: number | null;
}

function boundedLot(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= 100 ? trimmed : null;
}

function isoCalendarDate(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return null;
  const parsed = new Date(`${trimmed}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === trimmed
    ? trimmed
    : null;
}

/**
 * Chiama l'edge `ai-vision-proxy` in modalità LEAN `lot_expiry` (no-thinking,
 * veloce) con 1+ foto delle facce della stessa etichetta. Ritorna lotto,
 * scadenza e l'eventuale confidenza normalizzata 0..1. Lancia un Error leggibile
 * su quota/errore rete.
 */
export async function extractLotExpiryFromImages(
  images: string[],
  businessId?: string | null,
): Promise<LotExpiry> {
  const { data: sess } = await getSupabase().auth.getSession();
  const tok = sess.session?.access_token;
  if (!tok) throw new Error('Sessione scaduta. Esci e rientra.');
  const res = await fetch(`${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/ai-vision-proxy`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind: 'lot_expiry', images, business_id: businessId ?? undefined }),
  });
  const json = (await res.json()) as Record<string, unknown>;
  if (!res.ok) {
    const code = (json.error as string) ?? `HTTP ${res.status}`;
    if (code === 'quota_exceeded') throw new Error('Quota OCR esaurita. Riprova più tardi.');
    throw new Error(`Analisi etichetta non riuscita (codice: ${code}).`);
  }
  const rawResult = json.result ?? json;
  const r = rawResult && typeof rawResult === 'object' && !Array.isArray(rawResult)
    ? rawResult as Record<string, unknown>
    : {};
  const confidence = typeof r.confidence === 'number'
    && Number.isFinite(r.confidence)
    && r.confidence >= 0
    && r.confidence <= 1
    ? r.confidence
    : null;
  return {
    batch_lot: boundedLot(r.batch_lot),
    expiration_date: isoCalendarDate(r.expiration_date),
    confidence,
  };
}

/** Carica una foto etichetta nel bucket `lot-photos`; ritorna lo storage path. */
export async function uploadLotPhoto(tenantId: string, b64: string): Promise<string> {
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const path = `${tenantId}/${crypto.randomUUID()}.jpg`;
  const { error } = await getSupabase()
    .storage.from('lot-photos')
    .upload(path, bytes, { contentType: 'image/jpeg', upsert: false });
  if (error) throw error;
  return path;
}

/**
 * Scarica una foto etichetta da `lot-photos` come base64. Serve alla coda quando
 * l'app è stata riaperta e i base64 in memoria sono persi: l'estrazione riparte
 * dai path salvati.
 */
export async function downloadLotPhotoB64(path: string): Promise<string> {
  const { data, error } = await getSupabase().storage.from('lot-photos').download(path);
  if (error || !data) throw error ?? new Error('download_failed');
  const buf = new Uint8Array(await data.arrayBuffer());
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < buf.length; i += chunk) {
    bin += String.fromCharCode(...buf.subarray(i, i + chunk));
  }
  return btoa(bin);
}
